import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {FC, useCallback, useMemo} from 'react';

import {useOptionalStringArrayQueryString} from '@shared_web/lib/use_query_string';

import {FILTER_CONTROL_WIDTH, FILTER_LABEL_WIDTH} from '@website_frontend/components/filter_form';
import {useDbData} from '@website_frontend/lib/db_cache';

type Props = {
  title: string;
  paramName: string;
};

export const FilterTypeLabel: FC<Props> = ({title, paramName}) => {
  const {data} = useDbData();
  const [value, setValue] = useOptionalStringArrayQueryString(paramName);

  const uniqueTypeLabels = useMemo(() => {
    if (!data) return [];
    const labels = new Set<string>();
    data.forEach(item => {
      if (item.typeLabel) {
        labels.add(item.typeLabel);
      }
    });
    return Array.from(labels).sort();
  }, [data]);

  const handleChange = useCallback(
    (e: {target: {value: unknown}}) => {
      const newValue = e.target.value;
      // MUI Select with multiple returns string[] or string
      const selectedValues = typeof newValue === 'string' ? [newValue] : (newValue as string[]);
      setValue(selectedValues);
    },
    [setValue]
  );

  const renderValue = useCallback((selected: string[]) => {
    if (selected.length === 0) return 'Tous';
    if (selected.length === 1) return selected[0];
    return `${selected.length} sélectionnés`;
  }, []);

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="subtitle1" sx={{width: FILTER_LABEL_WIDTH, flexShrink: 0}}>
        {title}
      </Typography>
      <FormControl size="small" sx={{width: FILTER_CONTROL_WIDTH, minWidth: 0, flexGrow: 1}}>
        <InputLabel>{title}</InputLabel>
        <Select
          multiple
          value={value ?? []}
          onChange={handleChange}
          label={title}
          renderValue={renderValue}
        >
          {uniqueTypeLabels.map(label => (
            <MenuItem key={label} value={label}>
              <Checkbox checked={value && value.indexOf(label) > -1} />
              <ListItemText primary={label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
};

FilterTypeLabel.displayName = 'FilterTypeLabel';
